// Meta (Facebook) Pixel via fbevents.js. Mirrors the standard snippet: a `fbq`
// stub that queues calls until the real library loads and replays them.
const SCRIPT_ID = 'fb-pixel';

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

// Install the fbq stub + loader script once. Calls before the script loads queue
// on `fbq.queue` and replay when fbevents.js arrives.
function loadScript() {
  if (typeof document === 'undefined' || window.fbq || document.getElementById(SCRIPT_ID)) return;

  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue!.push(args);
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.push = fbq;
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

/** Load the pixel and initialize it. No-op without a pixel id. */
export function loadFacebookPixel(pixelId: string | undefined) {
  if (!pixelId) return;
  loadScript();
  window.fbq?.('init', pixelId);
}

/** Re-init with the uid as `external_id` (advanced matching). No-op until loaded. */
export function identifyFacebookPixel(user: { uid: string }, pixelId: string | undefined) {
  if (!pixelId || !window.fbq) return;
  window.fbq('init', pixelId, { external_id: user.uid });
}

/** Track a PageView for an SPA navigation. No-op until the pixel has loaded. */
export function trackFacebookPageView() {
  window.fbq?.('track', 'PageView');
}

/** Registration conversion. No-op until the pixel has loaded. */
export function trackFacebookCompleteRegistration() {
  window.fbq?.('track', 'CompleteRegistration', { content_name: 'user_registration' });
}

/** Subscribe conversion with value. No-op until the pixel has loaded. */
export function trackFacebookSubscribe(details: { value: number; currency: string }) {
  window.fbq?.('track', 'Subscribe', { value: details.value, currency: details.currency });
}
